import { takeLatest, fork, put, all, call } from "redux-saga/effects";

import { BOOTSTRAP_SESSION, FETCH_ME } from "./actionTypes";
import { meRequest, meSuccess, meFailure, sessionAnonymous } from "./actions";

import { getMe } from "../../../helpers/fakebackend_helper";
import { setSessionToken } from "../../../helpers/jwt-token-access/sessionToken";

function readAuthUser() {
  try {
    const raw = localStorage.getItem("authUser");
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

// Called on app mount. The session lives in an httpOnly cookie that JS cannot
// see, so /auth/me is the ONLY authority on whether a session exists — always
// ask it. The cached authUser marker must never gate the attempt: it gets
// wiped by any 401 blip (e.g. around a deploy), and gating on it declared
// perfectly valid cookie sessions "anonymous" without ever asking the server —
// which is what kept logging the founder out on every deploy/reload.
// A genuinely logged-out visitor just gets one cheap 401 -> anonymous.
function* bootstrapSession() {
  const cached = readAuthUser();
  // DASH-024 migration: a session created before this change still has the token
  // persisted in localStorage. Lift it into memory and scrub it from storage so
  // it's no longer XSS-readable, then carry on via the cookie.
  if (cached && cached.accessToken) {
    setSessionToken(cached.accessToken);
    const { accessToken, ...rest } = cached;
    try {
      localStorage.setItem("authUser", JSON.stringify({ ...rest, session: true }));
    } catch (_) { /* storage disabled — token already lifted into memory */ }
  }
  yield call(fetchMe);
}

export function* fetchMe() {
  yield put(meRequest());
  try {
    const response = yield call(getMe);
    const user = response && response.user;
    const permissions = (response && response.permissions) || [];
    if (!user) {
      yield put(sessionAnonymous());
      return;
    }
    yield put(meSuccess({ user, permissions }));
  } catch (error) {
    // Classify by HTTP STATUS, never by the text of the message. The old regex
    // (/Invalid credentials|invalid|expired/i) could not distinguish "the server
    // answered: you have no session" from "the server did not answer", so a 502
    // during a deploy, a 429, or one dropped packet all ended the session.
    //
    // Only an actual answer from the server may make a user anonymous:
    //   401 — token invalid/expired (authJwt.js)
    //   403 — "No token provided!" (authJwt.js:53) — the normal signed-out case
    // Anything transient (no response, 429, 5xx) is a TRANSPORT failure and must
    // leave the session intact so it can be retried. Keeping the 401/403 branch
    // is what stops a genuinely logged-out visitor from hanging on a spinner.
    const status = error && error.status;
    const message = typeof error === "string" ? error : (error && error.message) || "Failed to load profile";
    if (status === 401 || status === 403) {
      yield put(sessionAnonymous());
    } else {
      yield put(meFailure(message));
    }
  }
}

export function* watchProfile() {
  yield takeLatest(BOOTSTRAP_SESSION, bootstrapSession);
  yield takeLatest(FETCH_ME, fetchMe);
}

function* ProfileSaga() {
  yield all([fork(watchProfile)]);
}

export default ProfileSaga;
